// Builds the MUI theme from the company's server-driven colours, delivered on
// the logged-in user record (Users ⋈ CompanyInfo):
//   BackColor   → app background + surfaces   (Flutter colorFromAPI)
//   ButtonColor → header, buttons, device tiles (Flutter buttonFromAPI)
//   FontColor   → all foreground text          (Flutter textFromAPI)
// Colours arrive as Flutter ARGB (e.g. "0xFF8C8C8C"); normalizeHex handles that.

import { createTheme } from '@mui/material/styles';

export function normalizeHex(hex, fallback) {
  if (hex == null || hex === '') return fallback;
  // The server sends colours in Flutter ARGB form, e.g. "0xFF8C8C8C", and
  // sometimes as "#AARRGGBB", "RRGGBB", or "#RGB". Normalise all of them.
  let h = String(hex).trim()
    .replace(/^#/, '')       // drop a leading '#'
    .replace(/^0x/i, '');    // drop a Flutter '0x' prefix
  // 8 hex digits = AARRGGBB → drop the leading alpha byte.
  if (/^[0-9a-fA-F]{8}$/.test(h)) h = h.slice(2);
  return /^([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test(h) ? `#${h}` : fallback;
}

export function colorsFromUser(user) {
  const u = user || {};
  return {
    back: normalizeHex(u.BackColor, '#101820'),
    button: normalizeHex(u.ButtonColor, '#37474F'),
    text: normalizeHex(u.FontColor, '#FFFFFF'),
  };
}

function rgb(hex) {
  const h = String(hex).replace('#', '');
  const n = h.length === 3 ? h.split('').map((x) => x + x).join('') : h;
  return [
    parseInt(n.slice(0, 2), 16),
    parseInt(n.slice(2, 4), 16),
    parseInt(n.slice(4, 6), 16),
  ];
}

// Translucent variants of the font colour for borders / muted text, so the whole
// palette still derives from the record's three colours whatever they are.
function withAlpha(hex, a) {
  const [r, g, b] = rgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

const hex2 = (n) => Math.round(n).toString(16).padStart(2, '0');
const toHex = ([r, g, b]) => `#${hex2(r)}${hex2(g)}${hex2(b)}`;
const mix = (a, b, t) => {
  const x = rgb(a);
  const y = rgb(b);
  return toHex(x.map((v, i) => v + (y[i] - v) * t));
};

// WCAG 2.1 relative luminance.
function luminance(hex) {
  const [r, g, b] = rgb(hex).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast between two colours: 1 (identical) … 21 (black on white). */
export function contrastRatio(a, b) {
  const x = luminance(a);
  const y = luminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

/**
 * `ink` if it is legible on `bg`, otherwise the SMALLEST blend toward `toward`
 * that clears `min` — so a palette that is already readable is returned
 * untouched and one that is not gives up only as much hue as it has to.
 *
 * 4.5:1 is WCAG AA for body text, which is the size button labels are.
 *
 * Returns `toward` if even that does not clear the bar; there is nothing better
 * available from the three colours the record gives us.
 */
export function readableOn(ink, bg, toward, min = 4.5) {
  if (contrastRatio(ink, bg) >= min) return ink;
  for (let t = 1; t < 10; t += 1) {
    const c = mix(ink, toward, t / 10);
    if (contrastRatio(c, bg) >= min) return c;
  }
  return toward;
}

export function buildTheme(user) {
  const c = colorsFromUser(user);

  // ── The brand colour as INK, not as a surface ────────────────────────────
  //
  // ⚠️ ButtonColor IS A SURFACE COLOUR, AND HALF OF MUI USES IT AS AN INK.
  // A contained button and the app bar FILL with it and write FontColor on top —
  // which is the job it was picked for, and it always works. A text or outlined
  // button does the exact opposite: it writes ButtonColor ON BackColor. Nothing
  // makes those two far apart, and for at least one company in production they
  // are close enough that Cancel, "Upload a .bin", "Set & verify" and "What is
  // it running?" render as blue on blue and cannot be seen at all — while Save,
  // sitting right beside them, is perfectly legible because it fills.
  //
  // That is why it reads as a per-screen fault and is not one: every text and
  // outlined button in the app is affected, for that palette only.
  //
  // So ink is derived once here, giving up hue only as far as legibility
  // requires. A palette whose two colours are already far apart comes through
  // completely unchanged.
  const ink = readableOn(c.button, c.back, c.text);

  // eslint-disable-next-line no-console
  console.log('[Theme] applied colours', c, '(raw:', {
    BackColor: user && user.BackColor, ButtonColor: user && user.ButtonColor, FontColor: user && user.FontColor,
  }, ')', ink === c.button ? '' : `(button ink lifted to ${ink} for contrast)`);
  return createTheme({
    palette: {
      mode: 'dark',
      background: { default: c.back, paper: c.back },
      primary: { main: c.button, contrastText: c.text },
      secondary: { main: c.text, contrastText: c.button },
      text: { primary: c.text, secondary: withAlpha(c.text, 0.72) },
      divider: withAlpha(c.text, 0.24),
    },
    shape: { borderRadius: 10 },
    // Expose the raw colours so components can read them like the Flutter
    // globals. `ink` is the one to use for anything DRAWN ON the background —
    // an icon, a link, a bare label — rather than filled with the brand colour.
    sli: { ...c, ink },
    components: {
      // Header + all page app bars use the company Button colour with Font text.
      MuiAppBar: {
        defaultProps: { color: 'primary' },
        styleOverrides: { root: { backgroundColor: c.button, color: c.text } },
      },
      // Dialog / menu surfaces follow the background colour, not MUI's default.
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      // ⚠️ ONLY the variants that PAINT WITH the colour. `contained` keeps
      // ButtonColor exactly, because there it is the fill and the label on top
      // is FontColor — it was never the unreadable case, and lifting it would
      // change a brand colour nobody complained about. The `Primary` suffix
      // scopes these to the default colour, leaving error / warning / success
      // buttons — which carry their own contrast — untouched.
      MuiButton: {
        styleOverrides: {
          textPrimary: {
            color: ink,
            '&:hover': { backgroundColor: withAlpha(ink, 0.08) },
          },
          outlinedPrimary: {
            color: ink,
            borderColor: withAlpha(ink, 0.5),
            '&:hover': { borderColor: ink, backgroundColor: withAlpha(ink, 0.08) },
          },
        },
      },
      // Same reasoning: an icon button is ink on the background, never a fill.
      MuiIconButton: {
        styleOverrides: { colorPrimary: { color: ink } },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: c.button, color: c.text, border: `1px solid ${withAlpha(c.text, 0.24)}` },
          arrow: { color: c.button },
        },
      },
    },
  });
}
